import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { CRUDAction, PermissionType } from './core/enums';
import { AuthenticationGuard } from './core/guards/authentication.guard';
import { AuthorizationGuard } from './core/guards/authorization.guard';
import { AuthPageGuard } from './core/guards/authPage.guard';

const routes: Routes = [
  {
    path: 'auth',
    loadChildren: () =>
      import('./pages/auth/auth.module').then((m) => m.AuthPageModule),
    canActivate: [AuthPageGuard],
  },
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full',
  },
  {
    path: 'home',
    loadChildren: () =>
      import('./pages/home/home.module').then((m) => m.HomePageModule),
    canActivate: [AuthenticationGuard],
  },
  {
    path: 'dashboard',
    loadChildren: () => import('./pages/dashboard/dashboard.module').then( m => m.DashboardPageModule)
  },
  {
    path: 'doc-search',
    loadChildren: () =>
      import('./pages/doc-search/doc-search.module').then(
        (m) => m.DocSearchPageModule
      ),
    data: {
      permissionType: PermissionType.Page.valueOf(),
      permissionId: 2002,
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },
  {
    path: 'advanced-search',
    loadChildren: () =>
      import('./pages/advanced-search/advanced-search.module').then(
        (m) => m.AdvancedSearchPageModule
      ),
    data: {
      permissionType: PermissionType.Page.valueOf(),
      permissionId: 2002,
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },

  {
    path: 'catalog',
    loadChildren: () =>
      import('./pages/catalog/catalog.module').then((m) => m.CatalogPageModule),
    data: {
      permissionType: PermissionType.Page.valueOf(),
      permissionId: 2001,
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },
  {
    path: 'settlement-deeds',
    loadChildren: () =>
      import('./pages/settlement-deeds/settlement-deeds.module').then((m) => m.SettlementDeedsModule),
    data: {
      permissionType: PermissionType.Page.valueOf(),
      permissionId: 2001,
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },
  {
    path: 'settlement-deeds/:id',
    loadChildren: () => import('./pages/settlement-deeds-edit/settlement-deeds-edit.module').then(m => m.SettlementDeedsEditPageModule),
    data: {
      permissionType: PermissionType.Module.valueOf(),
      permissionId: 1002,
      crudAction: CRUDAction.Create.valueOf(),
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },
  {
    path: 'parcels',
    loadChildren: () =>
      import('./pages/parcels/parcels.module').then((m) => m.ParcelsPageModule),
    data: {
      permissionType: PermissionType.Module.valueOf(),
      permissionId: 1002,
      crudAction: CRUDAction.Create.valueOf(),
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },

  {
    path: 'parcels/:id',
    loadChildren: () =>
      import('./pages/parcels/parcels.module').then((m) => m.ParcelsPageModule),
    data: {
      permissionType: PermissionType.Module.valueOf(),
      permissionId: 1002,
      crudAction: CRUDAction.Read.valueOf(),
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },

  {
    path: 'buildings',
    loadChildren: () =>
      import('./pages/buildings/buildings.module').then(
        (m) => m.BuildingPageModule
      ),
    data: {
      permissionType: PermissionType.Module.valueOf(),
      permissionId: 1003,
      crudAction: CRUDAction.Create.valueOf(),
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },
  {
    path: 'buildings/:id',
    loadChildren: () =>
      import('./pages/buildings/buildings.module').then(
        (m) => m.BuildingPageModule
      ),
    data: {
      permissionType: PermissionType.Module.valueOf(),
      permissionId: 1003,
      crudAction: CRUDAction.Read.valueOf(),
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },
  {
    path: 'map',
    loadChildren: () =>
      import('./pages/map/map.module').then((m) => m.MapPageModule),
    data: {
      permissionType: PermissionType.Page.valueOf(),
      permissionId: 2005,
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },
  {
    path: 'reports',
    loadChildren: () =>
      import('./pages/reports/reports.module').then((m) => m.ReportsPageModule),
    data: {
      permissionType: PermissionType.Module.valueOf(),
      permissionId: 1006,
      crudAction: CRUDAction.Read.valueOf(),
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },
  {
    path: 'security',
    loadChildren: () =>
      import('./pages/security/security.module').then(
        (m) => m.SecurityPageModule
      ),
    data: {
      permissionType: PermissionType.Page.valueOf(),
      permissionId: 2000,
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },
  {
    path: 'other-catalogues',
    loadChildren: () =>
      import('./pages/other-catalogues/other-catalogues.module').then(
        (m) => m.OtherCataloguesPageModule
      ),
    data: {
      permissionType: PermissionType.Page.valueOf(),
      permissionId: 2006,
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },
  {
    path: 'searches',
    loadChildren: () =>
      import('./pages/searches/searches.module').then(
        (m) => m.SearchesPageModule
      ),
    data: {
      permissionType: PermissionType.Page.valueOf(),
      permissionId: 2006,
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },
  {
    path: 'buildings/:id/history',
    loadChildren: () =>
      import('./pages/audit-prop-search/audit-prop-search.module').then(
        (m) => m.AuditPropSearchPageModule
      ),
    data: {
      permissionType: PermissionType.Page.valueOf(),
      permissionId: 2003,
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },
  {
    path: 'parcels/:id/history',
    loadChildren: () =>
      import('./pages/audit-prop-search/audit-prop-search.module').then(
        (m) => m.AuditPropSearchPageModule
      ),
    data: {
      permissionType: PermissionType.Page.valueOf(),
      permissionId: 2003,
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },

  {
    path: 'password-reset',
    loadChildren: () =>
      import('./pages/password-reset/password-reset.module').then(
        (m) => m.PasswordResetPageModule
      ),
    canActivate: [],
  },

  {
    path: 'password-change/:token',
    loadChildren: () =>
      import('./pages/password-change/password-change.module').then(
        (m) => m.PasswordChangePageModule
      ),
    canActivate: [],
  },

  {
    path: 'e9',
    loadChildren: () =>
      import('./pages/e9/e9.module').then(
        (m) => m.E9PageModule
      ),
    data: {
      permissionType: PermissionType.Page.valueOf(),
      permissionId: 2007,
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },

  {
    path: 'search-fek',
    loadChildren: () =>
      import('./pages/search-fek/search-fek.module').then(
        (m) => m.SearchFekgeModule
      ),
  },
  {
    path: 'settlement-deeds',
    loadChildren: () =>
      import('./pages/settlement-deeds/settlement-deeds.module').then(
        (m) => m.SettlementDeedsModule
      ),
    data: {
      permissionType: PermissionType.Page.valueOf(),
      permissionId: 2009,
    },
    canActivate: [AuthenticationGuard, AuthorizationGuard],
  },

  { path: '**', redirectTo: 'home' },
  {
    path: 'modal-messages',
    loadChildren: () => import('./modal-messages/modal-messages.module').then(m => m.ModalMessagesPageModule)
  },


];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule { }
